import { useRef, useState, useEffect } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Rich } from "./Motion";
import { scrollToId } from "./SmoothScroll";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ text, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.35 + i * 0.13, ease: EASE }}>
      <Rich text={text} shimmer />
    </motion.span>
  </span>
);

const CLIPS = [
  { src: "/img/rome.jpg", label: "ROMA" },
  { src: "/img/paris.jpg", label: "PARIGI · PARIS" },
  { src: "/img/nyc.jpg", label: "NEW YORK" },
  { src: "/img/dubai.jpg", label: "DUBAI" },
  { src: "/img/singapore.jpg", label: "SINGAPORE" },
  { src: "/img/sydney.jpg", label: "SYDNEY" },
  { src: "/img/rio.jpg", label: "RIO DE JANEIRO" },
  { src: "/img/cairo.jpg", label: "IL CAIRO · CAIRO" },
  { src: "/img/london.jpg", label: "LONDON" },
];

const Montage = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % CLIPS.length), 4200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <AnimatePresence mode="popLayout">
        <motion.div key={i} className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1.16 }} exit={{ opacity: 0, scale: 1.2 }}
          transition={{ opacity: { duration: 1.4 }, scale: { duration: 5.6, ease: "linear" } }}>
          <img src={CLIPS[i].src} alt="" className="w-full h-full object-cover" />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0 bg-[#16265c]/60" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#16265c]/35 via-transparent to-[#16265c]/80" />
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 max-w-[60vw] truncate mono text-[10px] uppercase text-[rgba(246,241,232,0.8)] bg-[#16265c]/70 backdrop-blur px-2.5 py-1 rounded-sm">
        {CLIPS[i].label}
      </div>
    </div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const cX = useTransform(sx, (v) => v * 9);
  const cY = useTransform(sy, (v) => v * 6);
  const bgX = useTransform(sx, (v) => v * -16);
  const bgY = useTransform(sy, (v) => v * -10);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <section id="top" data-testid="hero-section" onMouseMove={onMove} className="relative min-h-[100svh] pt-28 lg:pt-36 pb-12 overflow-hidden grain bg-[var(--ink)] text-[var(--bone)]">
      <motion.div className="absolute inset-0" style={{ x: bgX, y: bgY }}>
        <Montage />
      </motion.div>
      <motion.div style={{ x: cX, y: cY }} className="relative max-w-[1440px] mx-auto px-5 lg:px-10">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mono text-[11px] sm:text-[12px] uppercase text-[var(--amber-2)] mb-8 lg:mb-10 flex items-center gap-2.5"><span className="inline-block w-2 h-2 rounded-full bg-[var(--amber-2)] animate-pulse shrink-0" />{t.hero.eyebrow}</motion.p>
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          <div className="lg:col-span-12 relative z-10">
            <h1 key={lang} data-testid="hero-title" className="display text-[13vw] sm:text-[10vw] lg:text-[5.9vw] leading-[1.02]">
              {t.hero.lines.map((l, i) => <MaskedLine key={i} text={l} i={i} />)}
            </h1>
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.6, delay: 0.9, ease: [0.76, 0, 0.24, 1] }} className="origin-left h-px bg-[var(--amber)] mt-12 lg:mt-14 max-w-[640px] relative overflow-hidden"><span aria-hidden="true" className="gold-line-sweep" /></motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.3, ease: EASE }} className="pt-8 max-w-[640px]">
              <p data-testid="hero-subtitle" className="text-base lg:text-lg leading-relaxed text-[rgba(246,241,232,0.9)]">{t.hero.sub}</p>
              <div className="flex flex-wrap gap-3 items-start mt-8">
                <button data-testid="hero-cta-contact" onClick={() => scrollToId("contact")} className="group inline-flex items-center gap-2 rounded-full bg-[var(--amber)] text-[var(--ink)] px-6 py-3.5 text-sm font-semibold hover:bg-[var(--fux)] hover:text-white transition-colors duration-300">
                  {t.hero.cta}<ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
                </button>
                <button data-testid="hero-cta-services" onClick={() => scrollToId("services")} className="group inline-flex items-center gap-2 rounded-full border border-[rgba(246,241,232,0.4)] px-6 py-3.5 text-sm hover:border-[var(--fux)] hover:text-[var(--fux-2)] transition-colors duration-300">
                  {t.hero.cta2}<ArrowDownRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
        <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }} className="flex flex-wrap gap-2 mt-14 relative z-10">
          {t.hero.tags.map((tag, i) => (
            <motion.li key={tag} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.7 + i * 0.12, ease: EASE }}>
              <span className="tag-float block mono text-[11px] uppercase px-3 py-1.5 border border-[rgba(246,241,232,0.3)] rounded-full text-[rgba(246,241,232,0.9)] backdrop-blur-sm hover:border-[var(--fux)] hover:text-[var(--fux-2)] transition-colors duration-300" style={{ animationDelay: `${i * 0.55}s` }}>{tag}</span>
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
      <div aria-hidden="true" className="hidden lg:flex absolute right-10 bottom-10 z-10 flex-col items-center gap-3">
        <span className="mono text-[10px] uppercase tracking-[0.3em] text-[rgba(246,241,232,0.65)]" style={{ writingMode: "vertical-rl" }}>Scroll</span>
        <span className="relative w-px h-14 bg-[rgba(246,241,232,0.25)] overflow-hidden">
          <span className="scroll-cue-dot absolute left-0 top-0 w-px h-4 bg-[var(--amber-2)]" />
        </span>
      </div>
    </section>
  );
};
